import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { format, startOfDay, endOfDay } from 'date-fns';
import {
  TimeEntry,
  DailyTimeSummary,
  WorkerClockStatus,
  TimeEntryFormData,
  ClockEventType,
  BreakReason,
  calculateMinutesDiff,
} from '@/types/timeTracking';

// =====================================================
// QUERIES
// =====================================================

// Obtener estado actual del usuario
export function useMyClockStatus() {
  const { userId } = useAuth();

  return useQuery({
    queryKey: ['my_clock_status', userId],
    queryFn: async (): Promise<WorkerClockStatus | null> => {
      if (!userId) return null;

      const today = format(new Date(), 'yyyy-MM-dd');

      // Get today's entries
      const { data: entries, error } = await api
        .from('time_entries')
        .select('*')
        .eq('user_id', userId)
        .gte('event_time', `${today}T00:00:00`)
        .lte('event_time', `${today}T23:59:59`)
        .order('event_time', { ascending: true });

      if (error) throw error;

      // Get user profile
      const { data: profile } = await api
        .from('profiles')
        .select('id, full_name, employee_id, area')
        .eq('id', userId)
        .single();

      // Get current shift assignment
      const { data: shiftData } = await api
        .from('worker_shift_assignments')
        .select(`
          shifts:shift_id (
            name,
            start_time,
            end_time
          )
        `)
        .eq('worker_id', userId)
        .lte('effective_date', today)
        .or([`end_date.is.null`, `end_date.gte.${today}`])
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingleRow();

      // Calculate current status
      let currentStatus: 'clocked_out' | 'working' | 'on_break' = 'clocked_out';
      let lastEventType: ClockEventType | null = null;
      let lastEventTime: string | null = null;
      let currentBreakReason: BreakReason | null = null;
      let todayWorkedMinutes = 0;
      let todayBreakMinutes = 0;

      if (entries && entries.length > 0) {
        const lastEntry = entries[entries.length - 1];
        lastEventType = lastEntry.event_type;
        lastEventTime = lastEntry.event_time;
        currentBreakReason = lastEntry.break_reason;

        if (lastEntry.event_type === 'clock_in' || lastEntry.event_type === 'break_end') {
          currentStatus = 'working';
        } else if (lastEntry.event_type === 'break_start') {
          currentStatus = 'on_break';
        } else if (lastEntry.event_type === 'clock_out') {
          currentStatus = 'clocked_out';
        }

        // Calculate worked and break minutes
        let workStart: Date | null = null;
        let breakStart: Date | null = null;

        for (const entry of entries) {
          const entryTime = new Date(entry.event_time);

          if (entry.event_type === 'clock_in') {
            workStart = entryTime;
          } else if (entry.event_type === 'clock_out' && workStart) {
            todayWorkedMinutes += calculateMinutesDiff(workStart.toISOString(), entryTime.toISOString());
            workStart = null;
          } else if (entry.event_type === 'break_start') {
            breakStart = entryTime;
            if (workStart) {
              todayWorkedMinutes += calculateMinutesDiff(workStart.toISOString(), entryTime.toISOString());
              workStart = null;
            }
          } else if (entry.event_type === 'break_end' && breakStart) {
            todayBreakMinutes += calculateMinutesDiff(breakStart.toISOString(), entryTime.toISOString());
            breakStart = null;
            workStart = entryTime;
          }
        }

        // If still working, add time until now
        if (currentStatus === 'working' && workStart) {
          todayWorkedMinutes += calculateMinutesDiff(workStart.toISOString(), new Date().toISOString());
        }
        if (currentStatus === 'on_break' && breakStart) {
          todayBreakMinutes += calculateMinutesDiff(breakStart.toISOString(), new Date().toISOString());
        }
      }

      const shift = (shiftData as any)?.shifts;

      return {
        user_id: userId,
        full_name: profile?.full_name || '',
        employee_id: profile?.employee_id || null,
        area: profile?.area || null,
        current_status: currentStatus,
        last_event_type: lastEventType,
        last_event_time: lastEventTime,
        today_worked_minutes: Math.round(todayWorkedMinutes),
        today_break_minutes: Math.round(todayBreakMinutes),
        current_break_reason: currentBreakReason,
        shift_name: shift?.name || null,
        shift_start: shift?.start_time || null,
        shift_end: shift?.end_time || null,
      };
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    enabled: !!userId,
  });
}

// Obtener entradas del día actual
export function useMyTodayEntries() {
  const { userId } = useAuth();
  const today = format(new Date(), 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['my_today_entries', userId, today],
    queryFn: async (): Promise<TimeEntry[]> => {
      if (!userId) return [];

      const { data, error } = await api
        .from('time_entries')
        .select('*')
        .eq('user_id', userId)
        .gte('event_time', `${today}T00:00:00`)
        .lte('event_time', `${today}T23:59:59`)
        .order('event_time', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });
}

// Obtener historial de fichajes de un usuario
export function useTimeEntries(
  userId?: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['time_entries', userId, startDate, endDate],
    queryFn: async (): Promise<TimeEntry[]> => {
      let query = api
        .from('time_entries')
        .select(`
          *,
          user:user_id (
            id,
            full_name,
            employee_id,
            area
          ),
          approver:approved_by (
            id,
            full_name
          )
        `)
        .order('event_time', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      }
      if (startDate) {
        query = query.gte('event_time', `${startDate}T00:00:00`);
      }
      if (endDate) {
        query = query.lte('event_time', `${endDate}T23:59:59`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: true,
  });
}

// Obtener estado de todos los trabajadores (para supervisores)
export function useAllWorkersClockStatus() {
  const today = format(new Date(), 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['all_workers_clock_status', today],
    queryFn: async (): Promise<WorkerClockStatus[]> => {
      // Get all active profiles
      const { data: profiles, error: profilesError } = await api
        .from('profiles')
        .select('id, full_name, employee_id, area')
        .eq('is_active', true)
        .order('full_name');

      if (profilesError) throw profilesError;

      // Get all today's entries
      const { data: entries, error: entriesError } = await api
        .from('time_entries')
        .select('*')
        .gte('event_time', `${today}T00:00:00`)
        .lte('event_time', `${today}T23:59:59`)
        .order('event_time', { ascending: true });

      if (entriesError) throw entriesError;

      // Get shift assignments
      const { data: shifts } = await api
        .from('worker_shift_assignments')
        .select(`
          worker_id,
          shifts:shift_id (
            name,
            start_time,
            end_time
          )
        `)
        .lte('effective_date', today)
        .or([`end_date.is.null`, `end_date.gte.${today}`]);

      const shiftsMap = new Map(
        (shifts || []).map((s: any) => [s.worker_id, s.shifts])
      );

      // Group entries by user
      const entriesByUser = new Map<string, any[]>();
      for (const entry of entries || []) {
        if (!entriesByUser.has(entry.user_id)) {
          entriesByUser.set(entry.user_id, []);
        }
        entriesByUser.get(entry.user_id)!.push(entry);
      }

      // Build status for each worker
      return (profiles || []).map((profile) => {
        const userEntries = entriesByUser.get(profile.id) || [];
        const shift = shiftsMap.get(profile.id);

        let currentStatus: 'clocked_out' | 'working' | 'on_break' = 'clocked_out';
        let lastEventType: ClockEventType | null = null;
        let lastEventTime: string | null = null;
        let currentBreakReason: BreakReason | null = null;
        let todayWorkedMinutes = 0;
        let todayBreakMinutes = 0;

        if (userEntries.length > 0) {
          const lastEntry = userEntries[userEntries.length - 1];
          lastEventType = lastEntry.event_type;
          lastEventTime = lastEntry.event_time;
          currentBreakReason = lastEntry.break_reason;

          if (lastEntry.event_type === 'clock_in' || lastEntry.event_type === 'break_end') {
            currentStatus = 'working';
          } else if (lastEntry.event_type === 'break_start') {
            currentStatus = 'on_break';
          }

          // Calculate times (simplified)
          let workStart: Date | null = null;
          let breakStart: Date | null = null;

          for (const entry of userEntries) {
            const entryTime = new Date(entry.event_time);

            if (entry.event_type === 'clock_in') {
              workStart = entryTime;
            } else if (entry.event_type === 'clock_out' && workStart) {
              todayWorkedMinutes += calculateMinutesDiff(workStart.toISOString(), entryTime.toISOString());
              workStart = null;
            } else if (entry.event_type === 'break_start') {
              breakStart = entryTime;
              if (workStart) {
                todayWorkedMinutes += calculateMinutesDiff(workStart.toISOString(), entryTime.toISOString());
                workStart = null;
              }
            } else if (entry.event_type === 'break_end' && breakStart) {
              todayBreakMinutes += calculateMinutesDiff(breakStart.toISOString(), entryTime.toISOString());
              breakStart = null;
              workStart = entryTime;
            }
          }

          if (currentStatus === 'working' && workStart) {
            todayWorkedMinutes += calculateMinutesDiff(workStart.toISOString(), new Date().toISOString());
          }
          if (currentStatus === 'on_break' && breakStart) {
            todayBreakMinutes += calculateMinutesDiff(breakStart.toISOString(), new Date().toISOString());
          }
        }

        return {
          user_id: profile.id,
          full_name: profile.full_name,
          employee_id: profile.employee_id,
          area: profile.area,
          current_status: currentStatus,
          last_event_type: lastEventType,
          last_event_time: lastEventTime,
          today_worked_minutes: Math.round(todayWorkedMinutes),
          today_break_minutes: Math.round(todayBreakMinutes),
          current_break_reason: currentBreakReason,
          shift_name: shift?.name || null,
          shift_start: shift?.start_time || null,
          shift_end: shift?.end_time || null,
        };
      });
    },
    refetchInterval: 30000,
  });
}

// =====================================================
// MUTATIONS
// =====================================================

// Registrar entrada de fichaje
export function useClockAction() {
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async (data: TimeEntryFormData) => {
      const { error } = await api.from('time_entries').insert({
        user_id: data.user_id || userId,
        event_type: data.event_type,
        event_time: data.event_time || new Date().toISOString(),
        break_reason: data.break_reason || null,
        break_type_id: data.break_type_id || null,
        notes: data.notes || null,
        is_manual: data.is_manual || false,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my_clock_status'] });
      queryClient.invalidateQueries({ queryKey: ['my_today_entries'] });
      queryClient.invalidateQueries({ queryKey: ['all_workers_clock_status'] });
      queryClient.invalidateQueries({ queryKey: ['time_entries'] });
    },
  });
}

// Aprobar/corregir entrada
export function useApproveTimeEntry() {
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async ({ entryId, approved }: { entryId: string; approved: boolean }) => {
      const { error } = await api
        .from('time_entries')
        .update({
          approved_by: approved ? userId : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', entryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time_entries'] });
    },
  });
}

// Eliminar entrada (solo admin)
export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await api
        .from('time_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my_clock_status'] });
      queryClient.invalidateQueries({ queryKey: ['my_today_entries'] });
      queryClient.invalidateQueries({ queryKey: ['all_workers_clock_status'] });
      queryClient.invalidateQueries({ queryKey: ['time_entries'] });
    },
  });
}

// =====================================================
// HELPERS
// =====================================================

export function getEventTypeLabel(type: ClockEventType): string {
  const labels: Record<ClockEventType, string> = {
    clock_in: 'Entrada',
    clock_out: 'Salida',
    break_start: 'Inicio Pausa',
    break_end: 'Fin Pausa',
  };
  return labels[type];
}

export function getEventTypeColor(type: ClockEventType): string {
  const colors: Record<ClockEventType, string> = {
    clock_in: 'bg-green-100 text-green-800',
    clock_out: 'bg-red-100 text-red-800',
    break_start: 'bg-yellow-100 text-yellow-800',
    break_end: 'bg-blue-100 text-blue-800',
  };
  return colors[type];
}

export function getStatusLabel(status: 'clocked_out' | 'working' | 'on_break'): string {
  const labels = {
    clocked_out: 'Fuera',
    working: 'Trabajando',
    on_break: 'En Pausa',
  };
  return labels[status];
}

export function getStatusColor(status: 'clocked_out' | 'working' | 'on_break'): string {
  const colors = {
    clocked_out: 'bg-gray-100 text-gray-800',
    working: 'bg-green-100 text-green-800',
    on_break: 'bg-yellow-100 text-yellow-800',
  };
  return colors[status];
}
